import { createAction } from '@reduxjs/toolkit'
import isNull from 'lodash/isNull'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import { updateSingleDocumentMapping } from '../effects/updateSingleDocumentMapping'
import { DocumentMapping } from '../../../models/documentMapping'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Overlay, Select } from '../../lib/toolBoxes/2D'
import { selectOpacityDict, selectOverlayColorDict } from '../../slices/2D'
import { selectDocumentMappingByActiveFloorId, selectDocumentMappings } from '../../slices/documents'
import { updateOverlaidChunkLockedFlag } from '../../slices/masterSetPlan'
import { Vector2D } from '../../types'

export interface LockOverlayPayload {
    documentChunkId: number | null
    /** Shared by every overlay in one Save; see `resolveOverlayLockGroupId`. */
    lockGroupId: string
    /** This overlay's position within its save batch. */
    lockOrder: number
}

export const lockOverlayAction = createAction<LockOverlayPayload>('lockOverlay')

export function* handleLockOverlay(action: ReturnType<typeof lockOverlayAction>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    const activeChunkMapping: DocumentMapping | null = yield select(selectDocumentMappingByActiveFloorId)

    const { documentChunkId, lockGroupId, lockOrder } = action.payload

    if (isNull(manager) || documentChunkId === null || !activeChunkMapping) return

    const [overlayTool]: [Overlay] = yield call(manager.getTools, [Overlay.NAME])

    const overlayData: {
        scale: number
        rotation: number
        position: Vector2D
    } | null = yield call(overlayTool.extractOverlayDataToSave, documentChunkId)

    if (!overlayData) return

    const mappings: DocumentMapping[] | null = yield select(selectDocumentMappings)

    if (!mappings) return

    const mapping = mappings.find((map) => map.document_chunk_id === documentChunkId)

    if (!mapping) return

    const opacityDict: Record<number, number> = yield select(selectOpacityDict)
    const colorDict: Record<number, string> = yield select(selectOverlayColorDict)
    const overlayOpacity = opacityDict[documentChunkId]
    const overlayColor = colorDict[documentChunkId]

    if (documentChunkId) {
        yield call(manager.useTool, Select.NAME)
    }

    // Re-locking an option already in this group keeps its order; a fresh one continues the base
    // home's sequence (-1 seed starts an unused base home at 0). The maximum counts the option
    // being locked: a batch reduces over one snapshot, so skipping it would collide two re-groups.
    const isReLockingSameGroup =
        mapping.additional_data.estimatedBaseHomeId === activeChunkMapping.id &&
        mapping.additional_data.estimatedBaseHomeLockGroupId === lockGroupId
    const existingLockOrder = mapping.additional_data.estimatedBaseHomeLockOrder
    const highestLockOrderAtBaseHome = mappings.reduce((highest, map) => {
        const order = map.additional_data.estimatedBaseHomeLockOrder

        if (
            map.additional_data.estimatedBaseHomeId === activeChunkMapping.id &&
            order !== undefined &&
            isFinite(order)
        ) {
            return Math.max(highest, order)
        }

        return highest
    }, -1)
    const estimatedBaseHomeLockOrder =
        isReLockingSameGroup && existingLockOrder !== undefined && isFinite(existingLockOrder)
            ? existingLockOrder
            : highestLockOrderAtBaseHome + 1 + lockOrder

    yield put(
        updateSingleDocumentMapping({
            ...mapping,
            overlay_center: overlayData.position,
            overlay_rotation: overlayData.rotation,
            overlay_scale: overlayData.scale,
            scale_factor: activeChunkMapping.scale_factor,
            additional_data: {
                ...mapping.additional_data,
                estimatedBaseHomeId: activeChunkMapping.id,
                estimatedBaseHomeLockOrder,
                estimatedBaseHomeLockGroupId: lockGroupId,
                ...(isFinite(overlayOpacity) ? { overlayOpacity } : {}),
                ...(overlayColor !== undefined ? { overlayColor } : {}),
            },
        })
    )

    yield call(overlayTool.lockOverlay, true)
    yield put(updateOverlaidChunkLockedFlag(true))
}

export function* watchForOverlayLocking() {
    yield takeEvery(lockOverlayAction.type, handleLockOverlay)
}
